import { createSlice, PayloadAction, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import {
    ChatCompletions,
    DisplaySection,
    DocumentTab,
    DocumentStatus,
    RenderableAsset,
    PendingDisplayDocumentChanges,
    ScoutDocumentStatus,
} from "@common/utils/types";
import { createTabId } from "@/common/utils/helpers";
import { RootState } from "@/app/store";

type ActiveDocuments = {
    allDocuments: ChatCompletions[];
    allDocumentTabs: DocumentTab[];
    activeTabId: string;
    activeDocumentIndex: number;
    progressStates: { [key: string]: number };
    activePatientRecordId: string;
    includedAssets: RenderableAsset[];
    // Shared in-flight guard for both entry points that create Monitoring documents
    // (PatientView's retry control and the sidebar's Generate button). Neither goes through
    // generateDocuments, so isGenerating never reflects them — without a shared lock per
    // patient, the two entry points could each fire their own concurrent batch call for the
    // same patient and chain duplicate day-documents that have no delete path in the UI.
    // Keyed by patientRecordId (not a single global flag) so an in-flight call for one
    // patient never blocks an unrelated call for a different patient.
    creatingMonitoringDocumentByPatient: { [patientRecordId: string]: boolean };
};

const initialState: ActiveDocuments = {
    allDocuments: [],
    allDocumentTabs: [],
    activeTabId: "",
    activeDocumentIndex: 0,
    progressStates: {},
    activePatientRecordId: "",
    includedAssets: [],
    creatingMonitoringDocumentByPatient: {},
};

/**
 * EN-1253: the tab that holds a document. A tab id keeps the header the tab was created with, but a
 * rename changes the document's header, so an id built from the current header can miss the tab or
 * name another tab of the same document type. Use the named tab only if it holds the document.
 */
const findTabForDocument = (state: ActiveDocuments, tabId: string, chatId: string | null | undefined) => {
    // An update without a document id names no document. Without this, `holds` would match any
    // document that has no id, in any tab, and the update would merge into it.
    if (chatId == null) return undefined;
    const holds = (t: DocumentTab) => t.documents?.some((doc) => doc.id === chatId);
    return state.allDocumentTabs.find((t) => t.tabId === tabId && holds(t)) ?? state.allDocumentTabs.find(holds);
};

const activeDocumentsSlice = createSlice({
    name: "activeDocuments",
    initialState,
    reducers: {
        setAllDocuments: (state, action: PayloadAction<ChatCompletions[]>) => {
            state.allDocuments = action.payload;
        },
        addToAllDocuments: (state, action: PayloadAction<{ document: ChatCompletions; patientRecordId: string }>) => {
            if (action.payload.patientRecordId !== state.activePatientRecordId) return;
            state.allDocuments.push(action.payload.document);
        },
        removeFromAllDocuments: (state, action: PayloadAction<{ documentId: string; patientRecordId: string }>) => {
            if (action.payload.patientRecordId !== state.activePatientRecordId) return;
            state.allDocuments = state.allDocuments.filter((doc) => doc.id !== action.payload.documentId);
        },
        setAllDocumentTabs: (state, action: PayloadAction<DocumentTab[]>) => {
            state.allDocumentTabs = action.payload;
        },
        addOrUpdateTab: (state, action: PayloadAction<Partial<DocumentTab>>) => {
            const { tabId } = action.payload;
            if (tabId) {
                const index = state.allDocumentTabs.findIndex((t) => t.tabId === tabId);
                if (index !== -1) {
                    // Update the tab at the found index
                    state.allDocumentTabs[index] = { ...state.allDocumentTabs[index], ...action.payload };
                } else {
                    // Add the new tab
                    state.allDocumentTabs.push(action.payload as DocumentTab);
                }
            }
        },
        setActiveTabId: (state, action: PayloadAction<string>) => {
            state.activeTabId = action.payload;
        },
        setActiveDocumentIndex: (state, action: PayloadAction<number>) => {
            state.activeDocumentIndex = action.payload;
        },
        addDocumentToTab: (state, action: PayloadAction<{ tabId: string; document: ChatCompletions }>) => {
            const { tabId, document } = action.payload;
            const tab = state.allDocumentTabs.find((t) => t.tabId === tabId);
            if (tab) {
                tab.documents.push(document);
            }
        },
        removeDocumentFromTab: (state, action: PayloadAction<{ tabId: string; documentId: string }>) => {
            const { tabId, documentId } = action.payload;
            const tab = state.allDocumentTabs.findIndex((t) => t.tabId === tabId);

            // tab not found
            if (tab === -1) return;

            // get current docs
            const docs = Array.isArray(state.allDocumentTabs[tab].documents)
                ? state.allDocumentTabs[tab].documents
                : [];

            const newDocuments = docs.filter((doc) => doc.id !== documentId);

            if (newDocuments.length > 0) {
                // replace docs and update active document index
                state.allDocumentTabs[tab].documents = newDocuments;
                if (state.activeTabId === tabId) {
                    state.activeDocumentIndex = Math.min(state.activeDocumentIndex, newDocuments.length - 1);
                }
            } else {
                // if no documents left, remove the tab
                state.allDocumentTabs.splice(tab, 1);

                if (state.allDocumentTabs.length) {
                    // set the active tab to the last one
                    const last = state.allDocumentTabs[state.allDocumentTabs.length - 1];
                    state.activeTabId = last.tabId;
                    // if somehow last.documents is missing, default to 1 so length-1 = 0
                    state.activeDocumentIndex = (last.documents?.length ?? 1) - 1;
                } else {
                    // no tabs left
                    state.activeTabId = "";
                    state.activeDocumentIndex = 0;
                }
            }
        },
        updateDocumentDataInTab: (
            state,
            action: PayloadAction<{ tabId: string; chatCompletionId: string; updatedData: string }>,
        ) => {
            const { tabId, chatCompletionId, updatedData } = action.payload;
            const tab = state.allDocumentTabs.find((t) => t.tabId === tabId);
            if (tab) {
                const docIndex = tab.documents.findIndex((chatCompletion) => chatCompletion.id === chatCompletionId);
                if (docIndex !== -1) {
                    tab.documents[docIndex].chat_json!.chat_text = updatedData;
                }
            }
        },
        updateDocumentIndex: (state, action: PayloadAction<{ tabId: string }>) => {
            const tab = state.allDocumentTabs.find((t) => t.tabId === action.payload.tabId);
            if (tab) {
                state.activeDocumentIndex = tab.documents.length - 1;
            }
        },
        //NOTE: Use this judiciously and only with partial/single values, or entire chat completion object updates
        updateDocumentInTab: (
            state,
            action: PayloadAction<{ tabId: string; document: Partial<ChatCompletions>; patientRecordId: string }>,
        ) => {
            const { tabId, document, patientRecordId } = action.payload;

            if (patientRecordId !== state.activePatientRecordId) return;

            // Drop archived payloads. After an optimistic Stop, a zombie completion realtime
            // event for the cancelled+archived row would otherwise resurrect the doc here.
            // Real INSERTs of new docs go through addOrUpdateTabAndAddDocument, not this reducer.
            if ((document as ChatCompletions).is_archived) return;

            const tab = findTabForDocument(state, tabId, document.id);
            if (!tab || !tab.documents) return;

            const docIndex = tab.documents.findIndex((doc) => doc.id === document.id);
            if (docIndex !== -1) {
                tab.documents[docIndex] = { ...tab.documents[docIndex], ...document };
            }
        },
        /**
         * Adds a new section to the document in the tab
         */
        updateDocumentSectionInTab: (
            state,
            action: PayloadAction<{
                tabId: string;
                chatId: string;
                section: DisplaySection;
                index: number;
                patientRecordId: string;
            }>,
        ) => {
            const { tabId, chatId, section, index, patientRecordId } = action.payload;

            if (patientRecordId !== state.activePatientRecordId) return;

            // Find the tab with the given tabId
            const tab = state.allDocumentTabs.find((t) => t.tabId === tabId);

            // If the tab is found, ensure documents is initialized and find the document with the given document id
            if (tab) {
                tab.documents = tab.documents || [];
                const docIndex = tab.documents.findIndex((doc) => doc.id === chatId);
                if (docIndex !== -1) {
                    const current = tab.documents[docIndex].chat_json!.chat_text;

                    if (current && Array.isArray(current)) {
                        // append it to current if it exists
                        tab.documents[docIndex].chat_json!.chat_text = joinCurrentAndSectionOnUpdate(
                            current,
                            section,
                            index,
                        );
                    } else {
                        // else create a new array with the section
                        tab.documents[docIndex].chat_json!.chat_text = [section];
                    }
                }
            }
        },
        setProgress: (state, action: PayloadAction<{ tabId: string; progress: number; patientRecordId: string }>) => {
            if (action.payload.patientRecordId !== state.activePatientRecordId) return;

            state.progressStates[action.payload.tabId] = action.payload.progress;
        },
        resetProgress: (state, action: PayloadAction<{ tabId: string; patientRecordId: string }>) => {
            if (action.payload.patientRecordId !== state.activePatientRecordId) return;

            delete state.progressStates[action.payload.tabId];
        },
        // creatingMonitoringDocumentByPatient is deliberately preserved across this reset: it's
        // a cross-patient-navigation in-flight lock on a shared backend action (see the field's
        // comment above), not per-tab view state. Resetting it while a call is still in flight
        // would drop the lock for a call that started on a different patient/tab and let a
        // second one fire concurrently, chaining duplicate day-documents with no UI delete path.
        resetActiveDocuments: (state) => ({
            ...initialState,
            creatingMonitoringDocumentByPatient: state.creatingMonitoringDocumentByPatient,
        }),
        setActivePatientRecordId: (state, action: PayloadAction<string>) => {
            state.activePatientRecordId = action.payload;
        },
        setChatCompletionStatus: (
            state,
            action: PayloadAction<{
                chatId: string;
                status: DocumentStatus;
                patientRecordId: string;
                header: string;
                documentTypeId: string;
            }>,
        ) => {
            if (action.payload.patientRecordId !== state.activePatientRecordId) return;

            const tabId = createTabId(action.payload.documentTypeId, action.payload.header);

            const tab = findTabForDocument(state, tabId, action.payload.chatId);
            if (tab) {
                const doc = tab.documents.find((doc) => doc.id === action.payload.chatId);
                if (doc) {
                    doc.status = action.payload.status;
                }
            }
        },
        setPendingDocument: (
            state,
            action: PayloadAction<{
                tabId: string;
                documentIndex: number;
                pendingDocumentChanges: PendingDisplayDocumentChanges | undefined;
            }>,
        ) => {
            const { tabId, documentIndex, pendingDocumentChanges } = action.payload;
            const tab = state.allDocumentTabs.find((t) => t.tabId === tabId);
            if (tab) {
                // Initialize pendingDocuments if it doesn't exist
                if (!tab.pendingDocuments) {
                    tab.pendingDocuments = {};
                }
                // If pendingDocumentChanges is undefined, remove the pending document
                if (!pendingDocumentChanges) {
                    delete tab.pendingDocuments[documentIndex];
                    return;
                }
                tab.pendingDocuments[documentIndex] = {
                    ...pendingDocumentChanges,
                    tabId,
                    documentIndex,
                };
            }
        },
        acceptPendingDocument: (state, action: PayloadAction<{ tabId: string; documentIndex: number }>) => {
            const { tabId, documentIndex } = action.payload;
            const tab = state.allDocumentTabs.find((t) => t.tabId === tabId);
            if (tab && tab.pendingDocuments?.[documentIndex]) {
                tab.pendingDocuments[documentIndex].accepted = true;
            }
        },
        setScoutDocumentStatus: (
            state,
            action: PayloadAction<{ tabId: string; documentIndex: number; status: ScoutDocumentStatus | undefined }>,
        ) => {
            const { tabId, documentIndex, status } = action.payload;
            const tab = state.allDocumentTabs.find((t) => t.tabId === tabId);
            if (tab) {
                // Initialize scoutDocumentStatus if it doesn't exist
                if (!tab.scoutDocumentStatus) {
                    tab.scoutDocumentStatus = {};
                }
                // If status is undefined, remove the entry
                if (status === undefined) {
                    delete tab.scoutDocumentStatus[documentIndex];
                    return;
                }
                tab.scoutDocumentStatus[documentIndex] = status;
            }
        },
        clearPendingDocuments: (state) => {
            // Clear all pending documents and scout document status from all tabs
            state.allDocumentTabs.forEach((tab) => {
                tab.pendingDocuments = {};
                tab.scoutDocumentStatus = {};
            });
        },
        setIsCreatingMonitoringDocument: (
            state,
            action: PayloadAction<{ patientRecordId: string; value: boolean }>,
        ) => {
            const { patientRecordId, value } = action.payload;
            if (value) {
                state.creatingMonitoringDocumentByPatient[patientRecordId] = true;
            } else {
                delete state.creatingMonitoringDocumentByPatient[patientRecordId];
            }
        },
    },
    extraReducers: (_builder) => {},
});

/**
 * Merges the current fields with the new fields
 * @param current The current fields
 * @param section The new fields
 * @param index The index to insert the new section
 * @returns The merged fields
 */
function joinCurrentAndSectionOnUpdate(
    current: DisplaySection[],
    section: DisplaySection,
    index: number,
): DisplaySection[] {
    // Check if the section already exists in current
    const existingSectionIndex = current.findIndex((c) => c.id === section.id);

    if (existingSectionIndex !== -1) {
        // If it exists, merge the fields
        const existingSection = current[existingSectionIndex];
        const newFields = (section.fields || []).map((field) => {
            const currentField = (existingSection.fields || []).find((f) => f.id === field.id);
            if (currentField) {
                return { ...currentField, ...field };
            }
            return field;
        });

        // Update the section in the current array
        const updatedCurrent = [...current];
        updatedCurrent[existingSectionIndex] = { ...existingSection, fields: newFields };
        return updatedCurrent;
    }

    // Ensure current has enough elements to accommodate the new section at the correct index
    const updatedCurrent = [...current];

    if (index >= 0) {
        // If index is greater than the current length, fill the gap with placeholders (e.g., null)
        while (updatedCurrent.length <= index) {
            updatedCurrent.push({} as DisplaySection); // or an empty object {}
        }

        // Now splice the section into the correct position
        updatedCurrent.splice(index, 1, section); // Replace the placeholder or insert if empty
    } else {
        // If index is not provided or invalid, push the section to the end
        updatedCurrent.push(section);
    }

    return updatedCurrent;
}

//ACTIONS
export const {
    setAllDocuments,
    setActiveTabId,
    setActiveDocumentIndex,
    setAllDocumentTabs,
    addDocumentToTab,
    updateDocumentIndex,
    updateDocumentDataInTab,
    updateDocumentSectionInTab,
    addOrUpdateTab,
    updateDocumentInTab,
    resetActiveDocuments,
    removeDocumentFromTab,
    setProgress,
    resetProgress,
    addToAllDocuments,
    removeFromAllDocuments,
    setActivePatientRecordId,
    setChatCompletionStatus,
    setPendingDocument,
    acceptPendingDocument,
    setScoutDocumentStatus,
    clearPendingDocuments,
    setIsCreatingMonitoringDocument,
} = activeDocumentsSlice.actions;

//SELECTORS
export const getAllDocumentTabs = (state: { activeDocuments: { allDocumentTabs: DocumentTab[] } }) =>
    state.activeDocuments.allDocumentTabs;

export const getActiveTabId = (state: { activeDocuments: { activeTabId: string } }) =>
    state.activeDocuments.activeTabId;

export const getActiveDocumentIndex = (state: { activeDocuments: { activeDocumentIndex: number } }) =>
    state.activeDocuments.activeDocumentIndex;

export const getCreatingMonitoringDocumentByPatient = (state: {
    activeDocuments: { creatingMonitoringDocumentByPatient: { [patientRecordId: string]: boolean } };
}) => state.activeDocuments.creatingMonitoringDocumentByPatient;

// Reads directly off whatever state object is passed in (either the reactive Redux state from
// useAppSelector, or a live store.getState() snapshot) so the same helper serves both the
// UI-facing "is this button disabled" boolean and the atomic race-check inside
// createMonitoringDocuments/startNextMonitoringDay.
export const isCreatingMonitoringDocumentForPatient = (
    state: { activeDocuments: { creatingMonitoringDocumentByPatient: { [patientRecordId: string]: boolean } } },
    patientRecordId: string,
) => !!state.activeDocuments.creatingMonitoringDocumentByPatient[patientRecordId];

export const getActiveDocument = createSelector(
    [getAllDocumentTabs, getActiveTabId, getActiveDocumentIndex], // input selectors
    (allDocumentTabs, activeTabId, activeDocumentIndex) => {
        // Find the DocumentTabs object for the active tab
        const activeTab = allDocumentTabs.find((tab) => tab.tabId === activeTabId);

        // If the active tab is found, return the document at the active index, otherwise return null
        return activeTab ? activeTab.documents[activeDocumentIndex] : null;
    },
);

export const getActiveDocuments = createSelector(
    [getAllDocumentTabs, getActiveTabId], // input selectors
    (allDocumentTabs, activeTabId) => {
        // Find the DocumentTabs object for the active tab
        const activeTab = allDocumentTabs.find((tab) => tab.tabId === activeTabId);

        // If the active tab is found, return its documents, otherwise return an empty array
        return activeTab ? activeTab.documents : [];
    },
);
// Memoized selector to get the length of the active documents
export const getActiveDocumentsLength = createSelector(
    [getActiveDocuments], // This should use the getActiveDocuments selector
    (activeDocuments) => {
        return activeDocuments.length; // Simply return the length of the active documents array
    },
);

// Selector to get pending document for the current document index
export const getPendingDocument = createSelector(
    [getAllDocumentTabs, getActiveTabId, getActiveDocumentIndex], // input selectors
    (allDocumentTabs, activeTabId, activeDocumentIndex) => {
        // Find the DocumentTabs object for the active tab
        const activeTab = allDocumentTabs.find((tab) => tab.tabId === activeTabId);

        // If the active tab is found, return the document at the active index, otherwise return null
        return activeTab ? activeTab.pendingDocuments[activeDocumentIndex] : undefined;
    },
);

export const getProgress = (state: { activeDocuments: ActiveDocuments }, tabId: string) =>
    state.activeDocuments.progressStates[tabId] || 0;

// Selector to check if the active document is generating
export const getIsGenerating = (state: { activeDocuments: ActiveDocuments }) => {
    const activeDoc = getActiveDocument(state);
    return activeDoc?.status === DocumentStatus.Processing;
};

// Selector to get every in-flight (processing/queued) document across all tabs.
// Used by the polling fallback to know what to wait on and when to stop.
export const getGeneratingDocuments = createSelector([getAllDocumentTabs], (allDocumentTabs) =>
    allDocumentTabs
        .flatMap((tab) => tab.documents ?? [])
        .filter((doc) => doc?.status === DocumentStatus.Processing || doc?.status === DocumentStatus.Queued),
);

// Selector to get scout document status for the active document
export const getScoutDocumentStatus = createSelector(
    [getAllDocumentTabs, getActiveTabId, getActiveDocumentIndex], // input selectors
    (allDocumentTabs, activeTabId, activeDocumentIndex) => {
        // Find the DocumentTabs object for the active tab
        const activeTab = allDocumentTabs.find((tab) => tab.tabId === activeTabId);

        // If the active tab and document are found, return the scout document status for the document index
        if (activeTab && activeDocumentIndex !== undefined && activeTab.scoutDocumentStatus) {
            return activeTab.scoutDocumentStatus[activeDocumentIndex];
        }
        return undefined;
    },
);

//Thunks
export const computeTabs = createAsyncThunk("activeDocuments/computeTabs", async (_, { getState, dispatch }) => {
    const state = getState() as { activeDocuments: ActiveDocuments };
    const { allDocuments } = state.activeDocuments;
    if (!allDocuments.length) return;

    const uniqueDocTypes = Array.from(
        new Set(
            allDocuments.map((doc: ChatCompletions) =>
                createTabId(doc?.document_type_id as string, doc.chat_json?.header || ""),
            ),
        ),
    );

    const newDocTypeMap = uniqueDocTypes.map((docType) => {
        const filteredDocs = allDocuments.filter(
            (doc: ChatCompletions) =>
                createTabId(doc?.document_type_id as string, doc.chat_json?.header || "") === docType,
        );

        const sortedDocs = filteredDocs.sort((a: ChatCompletions, b: ChatCompletions) => {
            const dateA = new Date(a.created_at!);
            const dateB = new Date(b.created_at!);
            return dateA.getTime() - dateB.getTime();
        });

        const header = sortedDocs[0]?.chat_json?.header || "";
        return {
            tabId: docType,
            header,
            documents: sortedDocs,
            pendingDocuments: {},
            scoutDocumentStatus: {},
            error: null,
        };
    });

    dispatch(setAllDocumentTabs(newDocTypeMap));
    dispatch(setActiveTabId(newDocTypeMap[0].tabId));
    dispatch(setActiveDocumentIndex(newDocTypeMap[0].documents.length - 1));
});

export const updateActiveTabAndDocumentIndex = createAsyncThunk(
    "activeDocuments/updateActiveTabAndDocumentIndex",
    async (tabId: string, { dispatch, getState }) => {
        dispatch(setActiveTabId(tabId));

        await Promise.resolve();
        const state = getState() as { activeDocuments: ActiveDocuments };
        const documents = getActiveDocuments(state); // This retrieves the documents for the newly active tab
        dispatch(setActiveDocumentIndex(documents.length - 1)); // Set the index to the last document
    },
);

export const addOrUpdateTabAndAddDocument = createAsyncThunk(
    "activeDocuments/addOrUpdateTabAndAddDocument",
    async (args: { tabId: string; document: ChatCompletions; patientRecordId: string }, { getState, dispatch }) => {
        const { tabId, document, patientRecordId } = args;
        const state = getState() as RootState;

        const { allDocumentTabs, activePatientRecordId } = state.activeDocuments;

        if (patientRecordId !== activePatientRecordId) return;

        // Check if the tab already exists
        const tab = allDocumentTabs.find((t) => t.tabId === tabId);

        if (!tab) {
            // If the tab doesn't exist, create a new tab with the document
            const newTab: DocumentTab = {
                tabId: tabId,
                header: document.chat_json?.header, // Assuming chat_json is always defined and has a header
                documents: [document],
                pendingDocuments: {},
                scoutDocumentStatus: {},
                error: null,
            };
            dispatch(addOrUpdateTab(newTab));
        } else {
            // If the tab exists, add the document to this tab
            dispatch(addDocumentToTab({ tabId, document }));
        }
        // Update the document index only once after handling the tab addition/update
        dispatch(updateDocumentIndex({ tabId }));
        dispatch(setActiveTabId(tabId));
    },
);

export const getIncludedAssets = createSelector(
    [
        (state: RootState) => state.activeDocuments.allDocumentTabs,
        (state: RootState) => state.activeDocuments.activeTabId,
        (state: RootState) => state.activeDocuments.activeDocumentIndex,
        (state: RootState) => state.activePatientData.transcriptSections,
        (state: RootState) => state.activePatientData.documentAssets,
    ],
    (allDocumentTabs, activeTabId, activeDocumentIndex, allTranscriptSections, allDocumentAssets) => {
        const activeTab = allDocumentTabs.find((tab) => tab.tabId === activeTabId);
        const activeDocument = activeTab ? activeTab.documents[activeDocumentIndex] : null;

        if (!activeDocument) return [];

        const includedTranscriptSectionIds = activeDocument.transcript_section_ids || [];
        const includedDocumentAssetIds = activeDocument.document_asset_ids || [];

        const includedTranscriptSections = allTranscriptSections
            .filter((section) => includedTranscriptSectionIds.includes(section.id))
            .map((section) => ({
                id: section.id,
                name:
                    section.display_name ||
                    new Date(section?.created_at || "").toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                type: "clip" as const,
                created_at: section.created_at || new Date().toISOString(),
            }));

        const includedDocumentAssets = allDocumentAssets
            .filter((asset) => includedDocumentAssetIds.includes(asset.id))
            .map((asset) => ({
                id: asset.id,
                name: asset.file_name,
                type: "file" as const,
                created_at: asset.created_at,
            }));

        return [...includedTranscriptSections, ...includedDocumentAssets].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
    },
);

export default activeDocumentsSlice.reducer;
